import { SyntheticEvent, useState } from "react";
import { Autocomplete, ListItem, styled, TextField, Typography } from "@mui/material";

export type OptionType = { id: string; label: string; color?: string; status?: string };

type AutoCompleteFilteredProps = {
  title?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: OptionType;
  options: OptionType[];
  renderOptionField?: keyof OptionType;
  numberOfOptions?: number;
  changeHandler: (e: SyntheticEvent<Element, Event>, v: OptionType) => void;
};

interface TypeLegendProps {
  color?: string;
  margin?: number;
}

const TypeLegend = styled("div")<TypeLegendProps>(({ theme, color, margin }) => {
  let bgColor = color;
  let show = true;
  if (!show) return null;

  return {
    minWidth: 8,
    maxWidth: 8,
    height: 20,
    backgroundColor: bgColor,
    borderRadius: 4,
    marginRight: margin ? margin : 4,
  };
});

const AutoCompleteFiltered = ({
  title,
  label,
  placeholder,
  disabled,
  value,
  options,
  numberOfOptions = 5,
  renderOptionField,
  changeHandler,
}: AutoCompleteFilteredProps) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  // MUI considers `undefined` uncontrolled. Always provide `null` when there
  // is no selected option so the autocomplete is controlled from first render.
  const selectedValue = value ?? null;

  const filtered = inputValue
    ? options
      .filter((el) => el.label.toLowerCase().includes(inputValue.toLowerCase()))
      .slice(0, numberOfOptions)
    : [];

  return (
    <>
      {title && (
        <Typography variant="h5" sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      <Autocomplete
        disabled={disabled}
        options={filtered}
        inputValue={inputValue}
        value={selectedValue}
        onChange={(e, v) => changeHandler(e, v as OptionType)}
        open={open}
        onOpen={() => {
          // allow opening only if something has been typed
          if (inputValue) setOpen(true);
        }}
        onClose={() => setOpen(false)}
        onInputChange={(_, v) => {
          setInputValue(v);
          // if user starts typing, open popup
          const isUserTyping = v && v.trim() !== (selectedValue?.label ?? "").trim();
          if (isUserTyping) {
            setOpen(true);
          } else {
            // clear input → hide popup
            setOpen(false);
          }
        }}
        fullWidth
        filterOptions={(x) => x} // Disable MUI default filter
        autoHighlight
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder ? placeholder : label}
            label={label ? label : null}
            sx={{ "& .MuiFormLabel-root": { fontSize: 14 } }}
            color="secondary"
            InputProps={{
              ...params.InputProps,
              startAdornment: selectedValue?.color ? (
                <TypeLegend color={selectedValue.color} margin={0} />
              ) : null,
            }}
          />
        )}
        renderOption={(props, option: any) => {
          const { key, ...optionProps } = props;
          return (
            <ListItem
              key={key}
              {...optionProps}
              sx={{
                "&&:hover": {
                  backgroundColor: "primary.lighter",
                },
              }}
            >
              <TypeLegend color={option.color} />
              {option[renderOptionField ? renderOptionField : "label"]}
            </ListItem>
          );
        }}
        sx={{
          // Root style
          "& .MuiOutlinedInput-root": {
            py: 0.4,
            minHeight: 41,
            flexWrap: "nowrap", // Prevent wrapping by default
            overflow: "hidden",
            alignItems: "center", // Vertically center the chips
          },
          "& .MuiFormLabel-root": {
            marginTop: "-5px",
          },
          "& .MuiInputLabel-shrink": {
            marginTop: "0px",
          },
        }}
      />
    </>
  );
};

export default AutoCompleteFiltered;
